/**
 * A change entry's "Show me" tour (docs/SYSTEM.md → Show me tours).
 *
 * Every entry from 0069 on says either which screens it changed —
 *
 *   tour:
 *     - route: /items?item={item}
 *       target: item-status
 *       caption: Each item now shows its status here.
 *
 * — or `tour: none` when nothing on screen changed. The test site walks the
 * steps in order, dimming everything but the `data-tour="…"` element.
 *
 * A route never names a real record: `{name}` is filled in at run time by a
 * placeholder registered in the app (src/features/shared/components/tour),
 * so a tour keeps working when that record is deleted (docs/LIVE-DATA.md).
 */
import { z } from 'zod'

/** Tours are required from this entry on; older ones are not backfilled. */
export const TOUR_REQUIRED_FROM = 69

/** `{item}` in a route — a record looked up when the tour runs. */
export const TOUR_PLACEHOLDER = /\{([a-z][a-z0-9-]*)\}/g

export const tourStepSchema = z.object({
  route: z
    .string()
    .regex(/^\/(?!\/)[^\s#]*$/, 'an app path starting with /, with any ?query, and no spaces or #'),
  target: z
    .string()
    .regex(/^[a-z][a-z0-9-]*$/, 'a data-tour name: lowercase words joined by dashes'),
  caption: z.string().min(8).max(160),
})

export type TourStep = z.infer<typeof tourStepSchema>

/** The steps, or `'none'` for a change with nothing to see. */
export type ChangeTour = TourStep[] | 'none'

const tourSchema = z.union([z.literal('none'), z.array(tourStepSchema).min(1)])

const unquote = (value: string) => value.replace(/^(["'])(.*)\1$/, '$2')

/**
 * Reads the `tour:` block out of an entry's frontmatter. `tour` is undefined
 * when there is none; `problem` says what is wrong with one that is there,
 * so changes.test.ts can name it instead of quietly dropping the button.
 */
export function parseChangeTour(frontmatter: string): { tour?: ChangeTour; problem?: string } {
  const lines = frontmatter.split(/\r?\n/)
  const start = lines.findIndex((line) => /^tour:/.test(line))
  if (start === -1) return {}

  const inline = (lines[start] ?? '')
    .replace(/^tour:/, '')
    .replace(/\s+#.*$/, '')
    .trim()
  let raw: unknown
  if (inline) {
    raw = inline
  } else {
    const steps: Record<string, string>[] = []
    for (const line of lines.slice(start + 1)) {
      // The block ends at the next top-level key.
      if (!/^\s/.test(line)) break
      const item = /^\s+(- )?([a-z]+):\s*(.*)$/.exec(line)
      if (!item) continue
      const [, dash, key = '', value = ''] = item
      if (dash || steps.length === 0) steps.push({})
      const step = steps.at(-1)
      // A trailing `# comment` is a note to the writer, as elsewhere in the frontmatter.
      if (step) step[key] = unquote(value.replace(/\s+#.*$/, '').trim())
    }
    raw = steps
  }

  const parsed = tourSchema.safeParse(raw)
  if (parsed.success) return { tour: parsed.data }
  return {
    problem: parsed.error.issues
      .map((issue) => `${issue.path.join('.') || 'tour'}: ${issue.message}`)
      .join('; '),
  }
}

/** The placeholder names a route uses — `{item}` → `item`. */
export function tourPlaceholders(route: string): string[] {
  return [...route.matchAll(TOUR_PLACEHOLDER)].map((match) => match[1] ?? '')
}
